import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import { PermissionService } from '../../lib/api'
import {
	GetComputedObjectPermissionsDataResponse,
	GetObjectPermissionsDataResponse,
} from '../../lib/api/types/permission.interfaces'
import { handleApiError } from '../../lib/utils/handleApiError'

export const useGetObjectPermissions = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetObjectPermissionsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetObjectPermissionsDataResponse>({
		queryKey: ['objectPermissions', id],
		queryFn: () => PermissionService.getAll({ id}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

export const useGetObjectComputedPermissions = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<
		QueryObserverOptions<GetComputedObjectPermissionsDataResponse>
	>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetComputedObjectPermissionsDataResponse>({
		queryKey: ['objectComputedPermissions', id],
		queryFn: () => PermissionService.getComputed({ id}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

export const useGetObjectUserPermissionLevel = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery({
		queryKey: ['objectUserPermissionLevel', id],
		queryFn: () => PermissionService.getUserLevel({ id}),
		enabled: isAuthenticated && Boolean(id),
		...options,
	})
}

export const useSelectSet = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['objectSelectPermissionsSet'],
		mutationFn: ({
			setId,
			id,
		}: {
			setId: string | null
			id: string
		}) => PermissionService.selectSet({ setId, id}),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { id}) => {
			queryClient.invalidateQueries({
				queryKey: ['objectPermissions', id],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
